import type { GeneratedStoryWithImageUrls } from 'schemas';
import type { EntityCardProps } from '@/components/EntityCard';
import { generatedStoriesPageMessages } from '../generatedStoriesPage.messages';

/**
 * Transforms generated story data to EntityCard component props
 * @param generatedStory - Generated story from the API
 * @returns Props object for EntityCard component
 */
export const transformGeneratedStoryToEntityCard = (
  generatedStory: GeneratedStoryWithImageUrls
): EntityCardProps => {
  // Helper function to get status display text
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'PENDING':
        return generatedStoriesPageMessages.statusPending;
      case 'COMPLETED':
        return generatedStoriesPageMessages.statusCompleted;
      case 'ERROR_TEXT':
        return generatedStoriesPageMessages.statusErrorText;
      case 'ERROR_IMAGES':
        return generatedStoriesPageMessages.statusErrorImages;
      case 'ERROR_PREVIEW':
        return generatedStoriesPageMessages.statusErrorPreview;
      case 'PREVIEW_COMPLETED':
        return generatedStoriesPageMessages.statusPreviewCompleted;
      default:
        return status;
    }
  };

  // Helper function to get generation mode display text
  const getGenerationModeDisplay = (mode: string) => {
    switch (mode) {
      case 'BASIC':
        return generatedStoriesPageMessages.generationModeBasic;
      case 'CUSTOM':
        return generatedStoriesPageMessages.generationModeCustom;
      default:
        return mode;
    }
  };

  // Format creation date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Prepare details - show template, creation date, and generation mode (status moved to tags)
  const details = [
    {
      label: generatedStoriesPageMessages.templateLabel,
      value: generatedStory.story_templates?.title || 'Unknown Template',
    },
    {
      label: generatedStoriesPageMessages.createdAtLabel,
      value: formatDate(generatedStory.created_at),
    },
    {
      label: generatedStoriesPageMessages.generationModeLabel,
      value: getGenerationModeDisplay(
        generatedStory.custom_prompt_data.generationMode
      ),
    },
  ];

  // Collect all image URLs: cover image + spread images
  const imageUrls: string[] = [];

  // Add cover image if available
  if (generatedStory.coverImageUrl) {
    imageUrls.push(generatedStory.coverImageUrl);
  }

  // Add spread images if available
  generatedStory.spreads.forEach((spread) => {
    if (spread.imageUrl) {
      imageUrls.push(spread.imageUrl);
    }
  });

  return {
    title: generatedStory.title,
    description: generatedStory.dedication || 'No dedication',
    imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
    imageAlt: `${generatedStory.title} images`,
    tags: [getStatusDisplay(generatedStory.generation_status)],
    details: details.length > 0 ? details : undefined,
  };
};
